import { useState, useEffect, useCallback } from "react"

export const COOKIE_CONSENT_KEY = "itacondo_cookie_consent_v1"
export const COOKIE_POLICY_VERSION = "2026-05-25"

export type CookieCategories = {
  necessary: boolean
  functional: boolean
  analytics: boolean
  marketing: boolean
}

export type CookieConsent = {
  categories: CookieCategories
  policyVersion: string
  acceptedAt: string
}

const DEFAULT: CookieCategories = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
}

export function useCookieConsent() {
  const [consent, setConsent] = useState<CookieConsent | null>(null)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    try {
      const raw = localStorage.getItem(COOKIE_CONSENT_KEY)
      if (raw) {
        const parsed: CookieConsent = JSON.parse(raw)
        if (parsed.policyVersion === COOKIE_POLICY_VERSION) {
          setConsent(parsed)
        }
      }
    } catch {}
    setLoaded(true)
  }, [])

  const save = useCallback((cats: CookieCategories) => {
    const next: CookieConsent = {
      categories: { ...cats, necessary: true },
      policyVersion: COOKIE_POLICY_VERSION,
      acceptedAt: new Date().toISOString(),
    }
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(next))
    setConsent(next)
    // Avisa outros componentes (analytics, marketing scripts)
    window.dispatchEvent(new CustomEvent("cookie-consent-changed", { detail: next }))
  }, [])

  const acceptAll = useCallback(() => {
    save({ necessary: true, functional: true, analytics: true, marketing: true })
  }, [save])

  const rejectAll = useCallback(() => {
    save({ ...DEFAULT })
  }, [save])

  const reset = useCallback(() => {
    localStorage.removeItem(COOKIE_CONSENT_KEY)
    setConsent(null)
  }, [])

  return { consent, loaded, hasDecided: !!consent, save, acceptAll, rejectAll, reset }
}
